import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { User, Mail, Phone, Lock, Plus, X, Camera, Award } from 'lucide-react';
import AuthImage from '../../components/AuthImage';
import toast from 'react-hot-toast';
import PageHeading from '../../components/PageHeading';
import WorkerIqpPanel from '../../components/WorkerIqpPanel';

function WorkerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Edit only when URL has a real id (workers/:id/edit). At workers/new, id can be undefined.
  const isEdit = id != null && id !== 'new' && String(id) !== 'undefined';
  // Valid worker id for API calls (avoid /workers/undefined/iqp)
  const effectiveWorkerId = (id && id !== 'new' && String(id) !== 'undefined') ? id : null;

  // Redirect if URL has invalid id (e.g. /workers/undefined/edit after create failed to get id)
  useEffect(() => {
    if (id && String(id) === 'undefined') {
      navigate('/people', { replace: true });
    }
  }, [id, navigate]);

  const [formData, setFormData] = useState({
    id: '',
    company_id: '',
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    phone: '',
    license_number: '',
    is_active: true,
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data.data || [];
    },
    enabled: !isEdit,
  });
  const companies = companiesData || [];

  const { data: worker, isLoading } = useQuery({
    queryKey: ['worker', id],
    queryFn: async () => {
      if (!id || id === 'new') {
        return null;
      }
      const response = await api.get(`/workers/${id}`);
      return response.data;
    },
    enabled: isEdit && effectiveWorkerId != null,
  });

  useEffect(() => {
    if (isEdit && worker) {
      setFormData({
        id: worker.id || '',
        company_id: worker.company_id || '',
        username: worker.username || '',
        password: '',
        confirmPassword: '',
        name: worker.name || '',
        email: worker.email || '',
        phone: worker.phone || '',
        license_number: worker.license_number || '',
        photo_url: worker.photo_url || '',
        is_active: worker.is_active !== undefined ? worker.is_active : true,
      });
    }
  }, [worker, isEdit]);

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const { confirmPassword, ...rest } = data;
      const submitData = { ...rest };
      if (submitData.id !== undefined && !String(submitData.id).trim()) {
        delete submitData.id;
      }
      if (submitData.company_id === '') {
        submitData.company_id = undefined;
      }
      console.log('[Worker Form] Creating worker with data:', submitData);
      try {
        const response = await api.post('/workers', submitData);
        console.log('[Worker Form] Worker created successfully:', response.data);
        return response.data;
      } catch (error) {
        console.error('[Worker Form] Error creating worker:', error);
        console.error('[Worker Form] Error response:', error.response?.data);
        console.error('[Worker Form] Error status:', error.response?.status);
        throw error;
      }
    },
    onSuccess: (data) => {
      const workerId = data?.id ?? data?.data?.id;
      if (!workerId) {
        console.error('[Worker Form] Create success but no worker id in response:', data);
        toast.success('Worker created. Refreshing list.');
        queryClient.invalidateQueries(['workers']);
        navigate('/people');
        return;
      }
      console.log('[Worker Form] Worker created successfully, navigating to:', workerId);
      toast.success('Worker created successfully');
      queryClient.invalidateQueries(['workers']);
      queryClient.invalidateQueries(['worker', workerId]);
      navigate(`/workers/${workerId}`);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create worker';
      console.error('[Worker Form] Create error:', errorMessage);
      toast.error(`Failed to create worker: ${errorMessage}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const { id: dataId, confirmPassword, ...submitData } = data;
      // Use URL param id when editing (always correct); fallback to form data
      const workerId = (isEdit && id) ? id : dataId;
      if (!workerId) {
        throw new Error('Worker ID is required for update');
      }
      // Only include password if it's provided
      if (!submitData.password || submitData.password === '') {
        delete submitData.password;
      }
      const response = await api.put(`/workers/${workerId}`, submitData);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Worker updated successfully');
      queryClient.invalidateQueries(['workers']);
      queryClient.invalidateQueries(['worker', id]);
      navigate(`/workers/${id}`);
    },
    onError: (error) => {
      const msg = error.response?.data?.error || error.message || 'Failed to update worker';
      toast.error(msg);
    },
  });

  // Soft delete (deactivate) worker
  const deactivateMutation = useMutation({
    mutationFn: async () => {
      const workerId = formData.id || id;
      const response = await api.put(`/workers/${workerId}`, {
        is_active: false,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Worker deactivated (hidden from active list)');
      queryClient.invalidateQueries(['workers']);
      if (id) {
        queryClient.invalidateQueries(['worker', id]);
      }
      navigate('/people');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to deactivate worker');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('[Worker Form] Form submitted, isEdit:', isEdit);
    console.log('[Worker Form] Form data:', formData);
    
    // For new workers: leave id empty to let backend generate (company/name initial + 4-digit serial)
    const submitData = { ...formData };
    if (!isEdit && !submitData.id?.trim()) {
      submitData.id = '';
      console.log('[Worker Form] Worker ID blank ??backend will generate from company_name/name');
    }

    if (!formData.username) {
      console.error('[Worker Form] Validation failed: Username is required');
      toast.error('Username is required');
      return;
    }
    
    if (!formData.name) {
      console.error('[Worker Form] Validation failed: Name is required');
      toast.error('Name is required');
      return;
    }
    
    if (!isEdit && !formData.password) {
      console.error('[Worker Form] Validation failed: Password is required');
      toast.error('Password is required');
      return;
    }
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      console.error('[Worker Form] Validation failed: Passwords do not match');
      toast.error('Passwords do not match');
      return;
    }
    
    console.log('[Worker Form] Validation passed, submitting...');
    if (isEdit) {
      console.log('[Worker Form] Calling updateMutation');
      updateMutation.mutate(formData);
    } else {
      console.log('[Worker Form] Calling createMutation');
      createMutation.mutate(submitData);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (isEdit && isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <PageHeading
        title={isEdit ? 'Edit Worker' : 'Add Worker'}
        backTo="/people"
        backLabel="Back to Staff"
        infoTitle="Worker"
        infoChildren={
          <p>
            Login account for the mobile app. IQP grades are managed per SS below; company affects auto-generated worker
            ID when left blank.
          </p>
        }
      />

      <form onSubmit={handleSubmit} noValidate className="bg-card rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Worker ID (only for new workers) */}
          {!isEdit && (
            <div>
              <label htmlFor="id" className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Worker ID (optional)
              </label>
              <input
                type="text"
                id="id"
                name="id"
                value={formData.id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Leave empty to auto-generate (e.g. A0001)"
              />
              <p className="text-xs text-gray-500 mt-1">Company initial (or worker name initial) + 4-digit serial</p>
            </div>
          )}

          {/* Company (from companies table ?? used when auto-generating Worker ID) */}
          {!isEdit && (
            <div>
              <label htmlFor="company_id" className="block text-sm font-medium text-gray-700 mb-2">
                Company (optional)
              </label>
              <select
                id="company_id"
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">None (Worker ID = worker name initial + 4 digits)</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">With company: Worker ID = company initial + 4-digit serial</p>
            </div>
          )}

          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Username * <span className="font-normal text-gray-500">(app login ID)</span>
            </label>
            <input
              type="text"
              id="username"
              name="username"
              required
              value={formData.username}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g. james, gidong"
            />
            <p className="text-xs text-gray-500 mt-1">
              Worker enters this username on the phone app ?? same account as the web.
            </p>
          </div>

          {/* Password ?? shared web + app */}
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3">
            <p className="text-sm font-medium text-amber-950">
              <Lock className="w-4 h-4 inline mr-1" />
              Login password (web + app)
            </p>
            <p className="text-xs text-amber-900/80">
              One password for both admin.cbsl.co.nz and the worker app. You can also set it under Staff ??Password.
            </p>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password {!isEdit && '*'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                required={!isEdit}
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                placeholder={isEdit ? 'Leave blank to keep current password' : 'Enter password'}
              />
              {isEdit && (
                <p className="text-xs text-gray-500 mt-1">Leave blank to keep the current password</p>
              )}
            </div>
            {formData.password && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  required={!!formData.password}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                  placeholder="Confirm password"
                />
              </div>
            )}
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter full name"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter email address"
            />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 inline mr-2" />
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter phone number"
            />
          </div>

          {/* License Number */}
          <div>
            <label htmlFor="license_number" className="block text-sm font-medium text-gray-700 mb-2">
              <Award className="w-4 h-4 inline mr-2" />
              License Number
            </label>
            <input
              type="text"
              id="license_number"
              name="license_number"
              value={formData.license_number}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter license number"
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
              Active Worker
            </label>
          </div>

          {/* Profile Photo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Camera className="w-4 h-4 inline mr-2" />
              Profile Photo
            </label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 rounded-lg border border-gray-300 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0">
                {formData.photo_url ? (
                  <AuthImage
                    filename={formData.photo_url}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      fd.append('type', 'workers');
                      const res = await api.post('/files/upload', fd);
                      const url = res.data?.url;
                      if (url) {
                        setFormData((prev) => ({ ...prev, photo_url: url }));
                        toast.success('Photo uploaded');
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Upload failed');
                    }
                    e.target.value = '';
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF, WebP. Save the form to keep the photo.</p>
              </div>
            </div>
          </div>
        </div>

        {/* SS Codes / IQP Management (only when we have a valid worker id) */}
        {isEdit && effectiveWorkerId && (
          <div className="mt-8 pt-8 border-t">
            <WorkerIqpPanel workerId={effectiveWorkerId} />
          </div>
        )}

        {/* Form Actions */}
        <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Deactivate / soft delete */}
          {isEdit && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Deactivate this worker and hide from list? (Data will not be deleted)')) {
                  deactivateMutation.mutate();
                }
              }}
              className="px-6 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50"
              disabled={deactivateMutation.isLoading}
            >
              {deactivateMutation.isLoading ? 'Deactivating...' : 'Disable (Soft Delete)'}
            </button>
          )}

          <div className="flex items-center justify-end gap-4 md:ml-auto">
            <button
              type="button"
              onClick={() => navigate('/people')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading
                ? 'Saving...'
                : isEdit
                ? 'Update Worker'
                : 'Create Worker'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default WorkerForm;
